import qs from 'query-string';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  EmailVerifyResponseType,
  MutationType,
  WorkflowType,
  WorkflowTypeOptions,
  WorkflowsResponseType,
} from 'shared/types';
import request from '../../../utils/request';
import { getWorkflowPayload } from './utils';
import i18n from '../../../i18n';

export const useWorkflows = () => {
  return useQuery<WorkflowsResponseType>(
    ['workflows'],
    async () => {
      const data = await request('/workflow/?page_size=100');
      return data;
    },
    {
      refetchOnMount: true,
    },
  );
};

export const useWorkflow = (id = '') => {
  return useQuery(
    ['workflow', id],
    async () => {
      if (!id) return;
      const data: WorkflowType = await request(`/workflow/${id}`);
      return data;
    },
    { enabled: !!id },
  );
};

export const useReorderWorkflows = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      payload,
      type,
    }: {
      payload: Record<string, number>;
      type: WorkflowTypeOptions;
    }) => {
      const data = await request(
        `/workflow/order?type=${type}`,
        payload,
        'POST',
      );
      return data;
    },
    {
      onSuccess: (response, variables) => {
        if (callback) callback(true, response, variables);
        queryClient.setQueryData(
          ['workflows'],
          (oldData?: WorkflowsResponseType) => {
            return {
              ...oldData,
              workflows: [...(oldData?.workflows || [])].map((wf) => ({
                ...wf,
                order: wf.workflow_id
                  ? variables?.payload[wf.workflow_id] ?? wf.order
                  : wf.order,
              })),
            };
          },
        );
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.workflow_reordered')}`,
          });
      },
      onError: (err, variables) => {
        if (callback) callback(false, err, variables);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.workflow_reorder_failed')}`,
          });
      },
    },
  );
};

export const useSaveWorkflow = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (workflow: WorkflowType) => {
      const data: WorkflowType = await request(
        workflow.workflow_id
          ? `/workflow/${workflow.workflow_id}`
          : '/workflow/',
        getWorkflowPayload(workflow),
        workflow.workflow_id ? 'PUT' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response, workflow) => {
        if (callback) callback(true, response, workflow);
        queryClient.setQueryData(
          ['workflow', `${response.workflow_id}`],
          response,
        );
        const workflowsData = queryClient.getQueryData(['workflows']);
        if (workflow.workflow_id && workflowsData !== undefined) {
          queryClient.setQueryData(
            ['workflows'],
            (oldData?: WorkflowsResponseType) => {
              return {
                ...oldData,
                workflows: [...(oldData?.workflows || [])].map((wf) =>
                  wf.workflow_id === response.workflow_id ? response : wf,
                ),
              };
            },
          );
        } else {
          queryClient.invalidateQueries('workflows');
          queryClient.refetchQueries('workflows');
        }
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: workflow.workflow_id
              ? `${i18n.t('forms.toast.success.workflow_updated')}`
              : `${i18n.t('forms.toast.success.workflow_created')}`,
          });
      },
      onError: (err: any, variables) => {
        if (callback) callback(false, err, variables);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text:
              err?.response?.data === 'duplicate workflow name'
                ? `${i18n.t('forms.toast.error.workflow_duplicate')}`
                : `${i18n.t('forms.toast.error.workflow_update_failed')}`,
          });
      },
    },
  );
};

export const useDeleteWorkflow = ({ setToast, callback }) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (workflowId: string) => {
      if (!workflowId) return;
      const data = await request(`/workflow/${workflowId}`, {}, 'DELETE');
      return data;
    },
    {
      onSuccess: (response, workflowId) => {
        if (callback) callback(true, response, workflowId);
        queryClient.removeQueries(['workflow', `${workflowId}`]);
        queryClient.setQueryData(
          ['workflows'],
          (oldData?: WorkflowsResponseType) => ({
            ...(oldData || {}),
            workflows: oldData?.workflows?.filter(
              (eachWorkflow) => eachWorkflow.workflow_id !== workflowId,
            ),
          }),
        );
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.workflow_deleted')}`,
          });
      },
      onError: (err, variables) => {
        if (callback) callback(false, err, variables);
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: `${i18n.t('forms.toast.error.workflow_delete_failed')}`,
          });
      },
    },
  );
};

export const useGetWorkflowPlaceholders = () => {
  return useQuery<Record<string, string[]>>(
    'workflow-placeholders',
    async () => {
      const data: Record<string, string[]> = await request(
        '/surveyserv/placeholders/workflow',
      );
      return data;
    },
  );
};

export const useGetEmailVerficationStatus = ({
  email,
  enabled,
}: MutationType & {
  email: string;
}) => {
  return useQuery(
    'email-verification-status',
    async () => {
      if (email) {
        const data: EmailVerifyResponseType = await request(
          `/surveyserv/email-verify/?email=${email}`,
        );
        return data;
      }
    },
    {
      onError: (error: any) => {
        if (error?.response?.status === 404) {
          return {
            is_verified: false,
            is_verify_pending: false,
          };
        }
      },
      enabled,
      staleTime: 10000,
      refetchOnWindowFocus: true,
      retry: 0,
    },
  );
};

export const useGetVerifiedEmails = () => {
  return useQuery(
    'verified-users',
    async () => {
      const data: string[] = await request('/surveyserv/verified-users');
      return data;
    },
    {
      staleTime: 10000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useVerifyEmail = ({ setToast, callback }: MutationType) => {
  return useMutation(
    async ({
      workflow_name,
      sender_email,
      sender_name,
    }: {
      workflow_name: string;
      sender_email: string;
      sender_name: string;
    }) => {
      if (!sender_email) return;
      const newURL = qs.stringifyUrl({
        url: '/surveyserv/email-verify',
        query: {
          email: sender_email || '',
          workflow_name: workflow_name || '',
          sender_name: sender_name || '',
        },
      });
      const data = await request(newURL, {}, 'POST');
      return data;
    },
    {
      onSuccess: (response: any, variables) => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: `${i18n.t('forms.toast.success.workflow_verify_email_sent')}`,
          });
        if (callback) callback(true, response, variables);
      },
      onError: (error: any, variables) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text:
              error.response.status === 429
                ? `${i18n.t('forms.toast.error.workflow_verify_email_limit')}`
                : `${i18n.t('forms.toast.error.workflow_verify_email_failed')}`,
          });
        if (callback) callback(false, error, variables);
      },
    },
  );
};
